import { request } from './core'
import type { AceSearchResponse, AssessmentState } from './types'

export const aceApi = {
  /** Open ACE opportunities matching `query` (case-insensitive substring on company
   *  name or id; empty → all open). Closed/lost opportunities are never returned. */
  searchAce: (slug: string, query = '') =>
    request<AceSearchResponse>(
      `/api/assessments/${slug}/ace/search?q=${encodeURIComponent(query)}`,
      { method: 'POST' },
    ),

  /** Pick an existing opportunity → hydrate the engagement metadata from it.
   *  `revision` is the assessment revision the caller acted on — REQUIRED by
   *  the server (428 without it, 412 → StaleStateError when stale), because
   *  linking re-points a record another session may have just changed. */
  selectAce: (slug: string, opportunityId: string, revision: number) =>
    request<{ applied: string[]; revision?: number; state: AssessmentState }>(
      `/api/assessments/${slug}/ace/select`,
      { method: 'POST', body: JSON.stringify({ opportunity_id: opportunityId }) },
      { ifMatch: revision },
    ),

  /** Stamp the AI Assessment program conventions (title + campaign) onto the
   *  linked opportunity and record the push (`ace_metadata_synced_at`). 409
   *  ace_not_ready without a linked opportunity. A non-idempotent AWS write,
   *  so `revision` (If-Match) is REQUIRED — a stale one 412s before AWS. */
  pushAceMetadata: (slug: string, revision: number) =>
    request<{ revision: number; state: AssessmentState }>(
      `/api/assessments/${slug}/ace/push-metadata`,
      { method: 'POST' },
      { ifMatch: revision },
    ),

  /** One Bedrock draft of the narrative creation-form fields (business
   *  problem, additional comments), grounded on the company profile. */
  draftAceOpportunity: (slug: string) =>
    request<{ fields: { business_problem: string; additional_comments: string } }>(
      `/api/companies/${slug}/ace/draft`,
      { method: 'POST' },
    ),

  /** Create a fully-specified opportunity (the dedicated ACE page's submit).
   *  Company-level; returns the new id — the page routes it via its return URL. */
  createAceOpportunity: (slug: string, body: CreateAceOpportunityBody) =>
    request<{ opportunity_id: string }>(`/api/companies/${slug}/ace/opportunity`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
}

export interface AceContactIn {
  name?: string
  title?: string
  email?: string
  phone?: string
}

export interface CreateAceOpportunityBody {
  company_name: string
  country_code: string
  title: string
  business_problem: string
  target_close_date: string
  industry?: string | undefined
  website?: string | undefined
  aws_account_id?: string
  city?: string
  state_or_region?: string
  postal_code?: string
  stage?: string
  opportunity_type?: string
  primary_needs?: string[]
  monthly_spend_usd?: string
  additional_comments?: string
  campaign_name?: string
  contacts?: AceContactIn[]
}
